// src/components/forms/PendingOrdersForm.tsx
// Componente refactorizado usando hooks y componentes modulares

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Departamentos from '../../../src/assets/data/departamentos.json'
import { metodoPago, transportadora } from '../../assets/data/arrays'
import Ciudades from '../../../src/assets/data/ciudades.json'
import { useCotizaciones } from '../../hooks/useCotizaciones'
import { CotizacionesModal } from '../modals/CotizacionesModal'
import ConfirmationModal from '../modals/ConfirmationModal'
import { PendingOrdersManualGuiaModal } from '../modals/PendingOrdersManualGuiaModal'
import { PendingOrdersGuiaModal } from '../modals/PendingOrdersGuiaModal'
import { Button } from '../ui'
import { PlusIcon, CalculatorIcon } from '@heroicons/react/24/outline'

// Tipos e interfaces
import type { PendingOrdersFormProps } from '../../types/pendingOrdersFormTypes'
import { tiposDeServicioLiquidacion } from '../../types/pendingOrdersFormTypes'

// Hooks
import { usePendingOrdersForm, usePendingOrdersProducts } from '../../hooks/pending-orders'

// Utilidades (cliente neto solo desde datos del pedido, sin switch en UI)
import { parseClienteNetoFlag } from '../../utils/clienteNetoFlag'

// Componentes de secciones
import {
  PendingOrdersOriginAddress,
  PendingOrdersDestinationAddress,
  PendingOrdersPackageInfo,
  PendingOrdersAdditionalInfo,
  PendingOrdersProductsSection
} from './pending-orders'

// Utilidades
import {
  renderDepartamentoOptions as renderDepartamentoOptionsUtil,
  renderCiudadOptions as renderCiudadOptionsUtil
} from '../../utils/pendingOrdersFormUtils'

const PendingOrdersForm: React.FC<PendingOrdersFormProps> = ({ 
  orderDetail, 
  isLiquidateMode = false,
  hideDestinationFields = [],
  hidePackageFields = [],
  hideProductSection = false
}) => {
  // Hook para manejar cotizaciones
  const {
    cotizaciones,
    todasLasCotizaciones,
    showModal: showCotizacionesModal,
    cotizar,
    cerrarModal,
    seleccionarCotizacion,
    showConfirmationModal,
    selectedCotizacion,
    confirmarCotizacion,
    cancelarCotizacion,
    showGuiaModal,
    guiaData,
    handleCloseGuiaModal,
    generatingGuia
  } = useCotizaciones()

  // Cliente neto: solo lectura desde el pedido (control oculto en cotizaciones de envío)
  const clienteNeto = useMemo(
    () => parseClienteNetoFlag(orderDetail?.extraDetails?.cliente_neto),
    [orderDetail?.id, orderDetail?.extraDetails?.cliente_neto]
  )

  const [selectedTipoServicio, setSelectedTipoServicio] = useState('3')

  // Estados para el modal de guía manual
  const [showManualGuiaModal, setShowManualGuiaModal] = useState(false)
  const [selectedTransportadora, setSelectedTransportadora] = useState('')
  const [numeroGuia, setNumeroGuia] = useState('')
  const [updatingGuia, setUpdatingGuia] = useState(false)

  // Función para calcular totales (necesaria para el hook de formulario)
  const calculateTotalsFn = useCallback((items: any[]) => {
    // Esta función será reemplazada por la del hook de productos
    return { total: '0' }
  }, [])

  // Hook de formulario
  const {
    formData,
    setFormData,
    handleInputChange: handleInputChangeForm,
    isOrigenExpanded,
    setIsOrigenExpanded,
    isDestinoExpanded,
    setIsDestinoExpanded,
    isPaqueteExpanded,
    setIsPaqueteExpanded,
    isAdicionalExpanded,
    setIsAdicionalExpanded,
    isFormReady,
    setIsFormReady,
    isInitialLoad
  } = usePendingOrdersForm({
    orderDetail,
    isLiquidateMode,
    departamentos: Departamentos,
    ciudades: Ciudades,
    calculateTotals: calculateTotalsFn
  })

  // Hook de productos
  const {
    productDetails,
    loadingDetails,
    expandedCombos,
    loadProductDetails,
    calculateTotals,
    getTotalFromProducts,
    toggleCombo
  } = usePendingOrdersProducts({
    orderDetail,
    formData,
    setFormData,
    onTipoServicioChange: setSelectedTipoServicio,
    onFormReadyChange: setIsFormReady,
    clienteNeto
  })

  // Handler para cambios en inputs con lógica adicional
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

      // Si se modificó el peso, actualizar automáticamente el tipo de servicio
      if (name === 'peso') {
      const peso = parseFloat(value) || 0
      const nuevoTipoServicio = (peso >= 0 && peso <= 8) ? '12' : '3'
      setSelectedTipoServicio(nuevoTipoServicio)
    }

    handleInputChangeForm(e)
  }, [handleInputChangeForm])

  // Efecto para cargar detalles de productos cuando cambia orderDetail
  useEffect(() => {
    // Solo cargar si hay lineItems
    // La verificación de cargas duplicadas se hace dentro de loadProductDetails
    if (orderDetail?.lineItems?.length) {
      loadProductDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail?.lineItems?.length])

  // Funciones para renderizar opciones
  const renderDepartamentoOptions = useCallback(() => {
    return renderDepartamentoOptionsUtil(formData.destinoDepartamento, Departamentos)
  }, [formData.destinoDepartamento])

  const renderCiudadOptions = useCallback(() => {
    return renderCiudadOptionsUtil(
      formData.destinoCiudad,
      formData.destinoDepartamento,
      Departamentos,
      Ciudades
    )
  }, [formData.destinoCiudad, formData.destinoDepartamento])

  // Handler para cotizar
  const handleCotizar = useCallback(async () => {
    try {
      await cotizar(formData, orderDetail, selectedTipoServicio)
    } catch (error) {
      console.error('Error al cotizar:', error)
    }
  }, [cotizar, formData, orderDetail, selectedTipoServicio])

  // Calcular total con flete
  const veificatotalconFlete = getTotalFromProducts()
  const totalconFlete = formData.metodoPago === 'cod' ? veificatotalconFlete.toString() : '0'

  // Handler para cerrar modal de guía manual
  const handleCloseManualGuiaModal = useCallback(() => {
    setShowManualGuiaModal(false)
    setSelectedTransportadora('')
    setNumeroGuia('')
  }, [])
  
  // Crear envío requiere pedido; cotizar envío puede usarse sin pedido seleccionado en lista
  if (!orderDetail && !isLiquidateMode) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-gray-600 text-lg font-medium">Selecciona un pedido</p>
        <p className="text-gray-500 text-sm mt-2">Para ver los detalles del formulario</p>
      </div>
    )
  }

  // Si el formulario no está listo Y no es la carga inicial, mostrar loading completo
  if (!isFormReady && !isInitialLoad) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-800 mb-4"></div>
        <p className="text-gray-600 text-lg font-medium">Preparando pedido...</p>
        <p className="text-gray-500 text-sm mt-2">Cargando detalles de productos y calculando medidas</p>
      </div>
    )
  }
  
  return (
    <>
      <form className="space-y-8">
        {/* Dirección de Origen */}
        <PendingOrdersOriginAddress
          formData={formData}
          isExpanded={isOrigenExpanded}
          onToggleExpanded={() => setIsOrigenExpanded(!isOrigenExpanded)}
          onInputChange={handleInputChange}
        />

        {/* Dirección de Destino */}
        <PendingOrdersDestinationAddress
          formData={formData}
          isExpanded={isDestinoExpanded}
          isLiquidateMode={isLiquidateMode}
          hideDestinationFields={hideDestinationFields}
          departamentos={Departamentos as any}
          ciudades={Ciudades}
          onToggleExpanded={() => setIsDestinoExpanded(!isDestinoExpanded)}
          onInputChange={handleInputChange}
          renderDepartamentoOptions={renderDepartamentoOptions}
          renderCiudadOptions={renderCiudadOptions}
        />

        {/* Información del Paquete (campos visibles según hidePackageFields) */}
        <PendingOrdersPackageInfo
          formData={formData}
          orderDetail={orderDetail}
          isExpanded={isPaqueteExpanded}
          isLiquidateMode={isLiquidateMode}
          hidePackageFields={hidePackageFields}
          selectedTipoServicio={selectedTipoServicio}
          tiposDeServicioLiquidacion={tiposDeServicioLiquidacion}
          metodoPago={metodoPago}
          totalconFlete={totalconFlete}
          onToggleExpanded={() => setIsPaqueteExpanded(!isPaqueteExpanded)}
          onInputChange={handleInputChange}
          onTipoServicioChange={setSelectedTipoServicio}
        />

        {/* Productos del Pedido */}
        <PendingOrdersProductsSection
          orderDetail={orderDetail}
          hideProductSection={hideProductSection}
          clienteNeto={clienteNeto}
          productDetails={productDetails}
          loadingDetails={loadingDetails}
          expandedCombos={expandedCombos}
          onToggleCombo={toggleCombo}
          calculateTotals={calculateTotals}
        />

        {/* Información Adicional (solo Crear envío) */}
        {!isLiquidateMode && (
          <PendingOrdersAdditionalInfo
            formData={formData}
            orderDetail={orderDetail}
            isExpanded={isAdicionalExpanded}
            onToggleExpanded={() => setIsAdicionalExpanded(!isAdicionalExpanded)}
            onInputChange={handleInputChange}
          />
        )}

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6">
          {!isLiquidateMode && (
            <Button
              type="button"
              variant="success"
              onClick={() => setShowManualGuiaModal(true)}
              leftIcon={PlusIcon}
              fullWidth
              className="sm:w-auto"
            >
              Agregar guia manual
            </Button>
          )}

          <Button
            type="button"
            variant="danger"
            onClick={handleCotizar}
            disabled={cotizaciones.loading || !formData.largo || !formData.ancho || !formData.altura || !formData.peso || !selectedTipoServicio}
            isLoading={cotizaciones.loading}
            leftIcon={CalculatorIcon}
            fullWidth
            className="sm:w-auto"
          >
            {cotizaciones.loading ? 'Cotizando...' : 'Cotizar'}
          </Button>
        </div>
      </form>

      {/* Modal de Cotizaciones */}
      <CotizacionesModal
        isOpen={showCotizacionesModal}
        onClose={cerrarModal}
        cotizaciones={cotizaciones}
        todasLasCotizaciones={todasLasCotizaciones}
        onSelectCotizacion={seleccionarCotizacion}
        loading={cotizaciones.loading}
      />

      {/* Modal de Guía Manual */}
      <PendingOrdersManualGuiaModal
        isOpen={showManualGuiaModal && !isLiquidateMode}
        orderDetail={orderDetail}
        selectedTransportadora={selectedTransportadora}
        numeroGuia={numeroGuia}
        updatingGuia={updatingGuia}
        transportadora={transportadora}
        onClose={handleCloseManualGuiaModal}
        onTransportadoraChange={setSelectedTransportadora}
        onNumeroGuiaChange={setNumeroGuia}
        onUpdatingChange={setUpdatingGuia}
      />

      {/* Modal de Guía Generada */}
      <PendingOrdersGuiaModal
        isOpen={showGuiaModal}
        guiaData={guiaData}
        onClose={handleCloseGuiaModal}
      />

      {/* Modal de Confirmación de Cotización */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={cancelarCotizacion}
        title="Confirmar Cotización"
        message={`¿Estás seguro de que deseas seleccionar la cotización de ${selectedCotizacion?.carrier} por $${selectedCotizacion?.precio.toLocaleString('es-CO')}?`}
        type="info"
        confirmText="Confirmar"
        cancelText="Cancelar"
        showCancel={true}
        onConfirm={() => confirmarCotizacion(formData, orderDetail, selectedTipoServicio)}
        icon={
          <svg className="mx-auto h-16 w-16 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />

      {/* Modal de Loading para Generación de Guía */}
      {generatingGuia && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-md w-full">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
              <h2 className="text-xl font-bold text-blue-800 mb-2">Generando Guía</h2>
              <p className="text-gray-600 text-center">
                Estamos generando la guía con {selectedCotizacion?.carrier}...
                <br />
                <span className="text-sm text-gray-500">Por favor espera un momento</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
};
export default PendingOrdersForm; 